import { useEffect, useState } from "react";
import { db, queue, uuid, type Point, type Contractor } from "../db";
import { getPosition } from "../lib";
import { sync } from "../sync";
import { Shell, type ScreenProps } from "./common";

const STATUSES = ["Переговоры", "Работаем", "Отказ", "Закрылась"];

export default function PointForm({ updateReady, back, state, user, pointId }: ScreenProps & { pointId?: string }) {
  const isNew = !pointId;
  const [form, setForm] = useState<Point>({
    id: pointId ?? uuid(), type: "pharmacy", name: "", network: "", ownership: null, contractorId: null,
    city: "Минск", district: "", address: "", category: "B", status: "Переговоры",
    lat: null, lon: null, note: "",
  });
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [networks, setNetworks] = useState<string[]>([]);
  const [geo, setGeo] = useState<"idle" | "busy" | "done" | "fail">("idle");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof Point>(k: K, v: Point[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    void (async () => {
      setContractors((await db.contractors.toArray()).filter((c) => !c.deletedAt)
        .sort((a, b) => a.name.localeCompare(b.name, "ru")));
      const pts = await db.points.toArray();
      setCities([...new Set(pts.map((p) => p.city).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ru")));
      setNetworks([...new Set(pts.map((p) => p.network).filter(Boolean) as string[])]
        .sort((a, b) => a.localeCompare(b, "ru")));
      if (pointId) {
        const p = await db.points.get(pointId);
        if (p) { setForm(p); setGeo(p.lat != null ? "done" : "idle"); }
      } else {
        // Для новой точки координаты берём сразу: сотрудник стоит на месте.
        setGeo("busy");
        const pos = await getPosition();
        if (pos) {
          setForm((f) => ({ ...f, lat: pos.coords.latitude, lon: pos.coords.longitude }));
          setGeo("done");
        } else setGeo("fail");
      }
    })();
  }, [pointId]);

  async function refreshGeo() {
    setGeo("busy");
    const pos = await getPosition();
    if (pos) {
      setForm((f) => ({ ...f, lat: pos.coords.latitude, lon: pos.coords.longitude }));
      setGeo("done");
    } else setGeo("fail");
  }

  async function save() {
    if (!form.name.trim()) return setError("Укажи название точки");
    if (!form.city.trim()) return setError("Укажи город");
    if (!form.address.trim()) return setError("Укажи адрес");
    setBusy(true); setError(null);
    try {
      const row: Point = {
        ...form,
        name: form.name.trim(), city: form.city.trim(), address: form.address.trim(),
        network: form.network?.trim() || null,
        district: form.district?.trim() || null,
        note: form.note?.trim() || null,
        // Чтобы новая точка сразу попала в «Мои», не дожидаясь обмена. Сервер
        // эти два поля от планшета не принимает и проставит сам.
        ...(isNew ? { createdById: user.id, brandId: user.brandId ?? null } : {}),
        updatedAt: new Date().toISOString(),
      };
      await db.points.put(row);
      await queue("point", row.id);
      void sync();
      back();
    } catch {
      setError("Не удалось сохранить");
      setBusy(false);
    }
  }

  return (
    <Shell
      title={isNew ? "Новая точка" : "Изменить точку"}
      onBack={back}
      state={state} updateReady={updateReady}
      footer={
        <>
          <button className="btn primary" onClick={save} disabled={busy}>Сохранить</button>
          <button className="btn ghost" onClick={back}>Отмена</button>
        </>
      }
    >
      {error && <div className="err">{error}</div>}

      <div className="field">
        <span className="label">Тип точки <span className="req">*</span></span>
        <div className="seg">
          {([["pharmacy", "💊 Аптека"], ["dental", "🦷 Стоматология"],
             ["podolog", "🦶 Подолог"], ["cosmetolog", "💆 Косметолог"]] as const)
            .map(([v, label]) => (
              <button key={v} type="button" aria-pressed={form.type === v}
                onClick={() => set("type", v)}>{label}</button>
            ))}
        </div>
      </div>

      {form.type === "dental" && (
        <div className="field">
          <span className="label">Форма собственности</span>
          <div className="seg">
            <button type="button" aria-pressed={form.ownership === "private"}
              onClick={() => set("ownership", "private")}>🦷 Частная</button>
            <button type="button" aria-pressed={form.ownership === "state"}
              onClick={() => set("ownership", "state")}>🏥 Государственная</button>
          </div>
          <div className="hint">От неё зависит значок в списке и фильтр</div>
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor="nm">Название <span className="req">*</span></label>
        <input id="nm" className="inp" value={form.name}
          placeholder="Например: Аптека «Мила» №8"
          onChange={(e) => set("name", e.target.value)} />
      </div>

      <div className="row2">
        <div className="field">
          <label className="label" htmlFor="nt">Сеть</label>
          <input id="nt" className="inp" list="nets" value={form.network ?? ""}
            placeholder="Начни печатать" onChange={(e) => set("network", e.target.value)} />
          <datalist id="nets">{networks.map((n) => <option key={n} value={n} />)}</datalist>
          <div className="hint">Розничная сеть. Пусто = одиночная</div>
        </div>
        <div className="field">
          <label className="label" htmlFor="ct">Контрагент</label>
          <select id="ct" className="inp" value={form.contractorId ?? ""}
            onChange={(e) => set("contractorId", e.target.value || null)}>
            <option value="">— не выбран —</option>
            {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="hint">От него подтянется матрица Lacalut</div>
        </div>
      </div>

      <div className="row2">
        <div className="field">
          <label className="label" htmlFor="cy">Город <span className="req">*</span></label>
          <input id="cy" className="inp" list="cities" value={form.city}
            onChange={(e) => set("city", e.target.value)} />
          <datalist id="cities">{cities.map((c) => <option key={c} value={c} />)}</datalist>
        </div>
        <div className="field">
          <label className="label" htmlFor="ds">Район</label>
          <input id="ds" className="inp" value={form.district ?? ""}
            placeholder="Необязательно" onChange={(e) => set("district", e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label className="label" htmlFor="ad">Адрес <span className="req">*</span></label>
        <input id="ad" className="inp" value={form.address}
          placeholder="ул. Притыцкого, 29" onChange={(e) => set("address", e.target.value)} />
      </div>

      <div className="row2">
        <div className="field">
          <label className="label" htmlFor="cg">Категория <span className="req">*</span></label>
          <select id="cg" className="inp" value={form.category}
            onChange={(e) => set("category", e.target.value as Point["category"])}>
            <option value="A">A — приоритетная</option>
            <option value="B">B — обычная</option>
            <option value="C">C — второстепенная</option>
          </select>
          <div className="hint">От неё зависит, как часто приходить</div>
        </div>
        <div className="field">
          <label className="label" htmlFor="st">Статус <span className="req">*</span></label>
          <select id="st" className="inp" value={form.status}
            onChange={(e) => set("status", e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className={"geo" + (geo === "done" ? "" : " pending")}>
        <span aria-hidden="true">📍</span>
        {geo === "busy" && "Определяем координаты…"}
        {geo === "done" && `Координаты определены (${form.lat?.toFixed(4)}, ${form.lon?.toFixed(4)})`}
        {geo === "fail" && "Координаты не определились — не страшно, точку можно сохранить"}
        {geo === "idle" && "Координаты не заданы"}
        <button className="btn tiny" style={{ marginLeft: "auto" }} onClick={refreshGeo}>
          Обновить
        </button>
      </div>

      <div className="field">
        <label className="label" htmlFor="nо">Комментарий</label>
        <textarea id="nо" className="inp" style={{ minHeight: 80 }} value={form.note ?? ""}
          placeholder="Как проехать, часы работы, что важно помнить"
          onChange={(e) => set("note", e.target.value)} />
      </div>
    </Shell>
  );
}
